import React, { ChangeEvent, KeyboardEvent, useState } from 'react';
import IOption from 'models/Option';
import { FaEye, FaEyeSlash } from "react-icons/fa";

type DefaultProps = {
  inputRef?: any;
  className?: string;
  id: string;
  label?: string;
  placeholder?: string;
  name: string;
  value: string | number | undefined;
  onBlur?: (event:React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (event:ChangeEvent<HTMLInputElement>) => void;
  required?: boolean;
  error?: boolean;
  errorText?: string;
  helperText?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  min?: number;
}

type InputProps = DefaultProps & {
  type?: 'text' | 'email' | 'password' | 'number' | 'date' | 'time' | 'datetime-local';
  min?: number;
  max?: number;
  onKeyPress?: (event:KeyboardEvent<HTMLInputElement>) => void;
  decimal?: boolean;
};

export const Input = React.forwardRef<any, InputProps>(({
  className = 'mb-4', id, label, value = '', required, disabled, error,
  errorText, helperText, onChange, onKeyPress = () => {}, type, decimal = false, ...props
}, ref) => {

  const [showPassword, setShowPassword] = useState(false);

  const handleOnChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!onChange) return;

    if (type === 'number') {
      const value = e.target.value;
      const numberRegex = decimal ? /^-?\d*\.?\d*$/ : /^\d*$/;
      if (!numberRegex.test(value)) return;
    }

    onChange(e);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!onKeyPress) return;

    if (type === 'number') {
      if (decimal) {
        const allowedKeys = ['0','1','2','3','4','5','6','7','8','9','.'];
        if (!allowedKeys.includes(e.key)) {
          e.preventDefault();
        }
      } else {
        if (/\D/.test(e.key)) {
          e.preventDefault();
        }
      }
    }

    onKeyPress(e);
  };

  const isPassword = type === 'password';

  return (
    <div className={className}>
      {label ? (
        <label
          htmlFor={id}
          className={`
            block text-white text-sm font-semibold mb-1
            ${disabled ? 'opacity-50' : ''}
            ${error ? 'text-red-500' : '' }
          `}
        >
          {label}{required ? ' *' : ''}
        </label>
      ) : null}

      <div className="relative">
        <input
          {...props}
          ref={ref}
          className={`
            control
            ${error ? 'border-red-500' : ''}
            ${isPassword ? 'pr-10' : ''}
          `}
          id={id}
          type={isPassword && showPassword ? 'text' : type}
          value={value}
          onChange={handleOnChange}
          onKeyPress={handleKeyPress}
          disabled={disabled}
        />

        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 text-gray-300 hover:text-white"
            style={{ transform: 'translateY(-50%)' }}
            tabIndex={-1}
          >
            {showPassword ? <FaEyeSlash /> : <FaEye />}
          </button>
        )}
      </div>

      {error && errorText ? (
        <span className="block text-red-500 text-sm py-2">{errorText}</span>
      ) : null}

      {helperText ? (
        <span className="block text-gray-400 text-sm font-light leading-5 mt-2">
          {helperText}
        </span>
      ) : null}
    </div>
  )
});

type SelectProps = Omit<DefaultProps, 'onChange' | 'onBlur'> & {
  options: IOption[];
  onBlur?: (event:React.FocusEvent<HTMLSelectElement>) => void;
  onChange?: (event:React.ChangeEvent<HTMLSelectElement>) => void;
};

export const Select = React.forwardRef<any, SelectProps>(({
  className = 'mb-4', id, label, value = '', options, required, disabled, error, errorText, helperText, ...props
}, ref) => {
  return (
    <div className={className}>
      {label ? (
        <label
          htmlFor={id}
          className={`
            block text-white text-sm font-semibold mb-1
            ${disabled ? 'opacity-50' : ''}
            ${error ? 'text-red-500' : '' }
          `}
        >{label}{required ? ' *' : ''}</label>
      ) : null}
      <select
        {...props}
        ref={ref}
        className={`
          control
          ${error ? 'border-red-500' : ''}
        `}
        id={id} value={value} disabled={disabled}
      >
        {options.map((option, index:number) => (
          <option key={`${props.name}-option-item-${index}`} value={option.value} disabled={option.disabled}>{option.label}</option>
        ))}
      </select>
      {error && errorText ? <span className="block text-red-500 text-sm py-1">{errorText}</span> : null}
      {helperText ? <span className="block text-gray-400 text-sm font-light leading-5 mt-2">{helperText}</span> : null}
    </div>
  )
});

type TextareaProps = Omit<DefaultProps, 'onChange' | 'onBlur'> & {
  rows?: number;
  onBlur?: (event:React.FocusEvent<HTMLTextAreaElement>) => void;
  onChange: (event:React.ChangeEvent<HTMLTextAreaElement>) => void;
  onKeyPress?: (event:KeyboardEvent<HTMLTextAreaElement>) => void;
  onKeyDown?: (event:KeyboardEvent<HTMLTextAreaElement>) => void;
};

export const Textarea = React.forwardRef<any, TextareaProps>(({
  className = 'mb-4', id, label, value = '', rows = 5, required, disabled, error, errorText, helperText, onKeyPress, ...props
}, ref) => {
  return (
    <div className={className}>
      {label ? (
        <label
          htmlFor={id}
          className={`
            block text-white text-sm font-semibold mb-1
            ${disabled ? 'opacity-50' : ''}
            ${error ? 'text-red-500' : '' }
          `}
        >{label}{required ? ' *' : ''}</label>
      ) : null}
      <textarea
        {...props}
        ref={ref}
        className={`
          control
          ${error ? 'border-red-500' : ''}
        `}
        onKeyPress={onKeyPress}
        id={id} value={value} rows={rows} disabled={disabled} 
      ></textarea>
      {error && errorText ? <span className="block text-red-500 text-sm py-1">{errorText}</span> : null}
      {helperText ? <span className="block text-gray-400 text-sm font-light leading-5 mt-2">{helperText}</span> : null}
    </div>
  )
})

type CheckboxProps = {
  inputRef?: any;
  className?: string;
  id: string;
  name: string;
  checked: boolean;
  label: string;
  onClick?: (event:React.MouseEvent<HTMLInputElement>) => void;
  onBlur?: (event:React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event:React.ChangeEvent<HTMLInputElement>) => void;
  helperText?: string;
  disabled?: boolean;
};

export const Checkbox = React.forwardRef<any, CheckboxProps>(({
  className = 'mb-4', id, label, helperText, disabled = false, ...props
}, ref) => {
  return (
    <div className={className}>
      <label className="flex items-center" htmlFor={id}>
        <input
          {...props}
          ref={ref}
          id={id}
          type="checkbox"
          disabled={disabled}
        />
        <span className="ml-2 text-sm text-white">{label}</span>
      </label>
      {helperText ? <span className="block text-gray-400 text-sm font-light leading-5 mt-2">{helperText}</span> : null}
    </div>
  )
});

type RadioProps = {
  inputRef?: any;
  className?: string;
  id: string;
  name: string;
  checked: boolean;
  label: string;
  onClick?: (event:React.MouseEvent<HTMLInputElement>) => void;
  onBlur?: (event:React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event:React.ChangeEvent<HTMLInputElement>) => void;
  helperText?: string;
};

export const Radio = React.forwardRef<any, RadioProps>(({
  className = 'mb-4', id, label, helperText, ...props
}, ref) => {
  return (
    <div className={className}>
      <label className="flex items-center" htmlFor={id}>
        <input
          {...props}
          ref={ref}
          id={id}
          type="radio"
        />
        <span className="ml-2 text-base text-white">{label}</span>
      </label>
      {helperText ? <span className="block text-gray-400 text-sm font-light leading-5 mt-2">{helperText}</span> : null}
    </div>
  )
});

type ToggleProps = {
  inputRef?: any;
  className?: string;
  label?: string;
  id: string;
  checked: boolean;
  onClick?: (event:React.MouseEvent<HTMLInputElement>) => void;
  onBlur?: (event:React.FocusEvent<HTMLInputElement>) => void;
  onChange: (event:React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
};

export const Toggle:React.FC<ToggleProps> = ({
  // Props
  className = '', label, id, disabled = false, ...props
}) => {
  return (
    <label className={`toggle ${className}`} htmlFor={id}>
      <input
        {...props}
        className="toggle-control"
        id={id} type="checkbox"
        disabled={disabled}
      />
      <span className="toggle-icon"></span>
      {label ? (
        <span className="toggle-label">{label}</span>
      ) : null}
    </label>
  )
}

